import type {
  Placement as FloatingPlacement,
  Strategy,
} from "@floating-ui/react-dom";
import {
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import type { ReactNode, RefObject } from "react";
import React, { useLayoutEffect, useRef } from "react";
import { styled } from "../../../theme/styled";
import type { KivraCSSObject } from "../../../theme/styled/styled";
import { zIndex } from "../../../theme/zIndex";
import { useDismiss } from "../../../util/useDismiss/useDismiss";
import { useIsDarkMode } from "../../../util/useIsDarkMode";
import { useMountTransition } from "../../../util/useMountTransition/useMountTransition";
import { StyledPopoverBase } from "./Base";

/**
 * We need to Polyfill ResizeObserver which is used by FloatingUI (if it doesn't exist).
 */
if (!("ResizeObserver" in window)) {
  (window as Window & typeof globalThis).ResizeObserver =
    ResizeObserverPolyfill;
}

type Placement = FloatingPlacement;

export interface PopperProps {
  isOpen: boolean;
  onClose: (focusTextField?: boolean) => void;
  children: ReactNode;
  anchorRef: RefObject<HTMLElement | null>;
  dataTestId?: string;
  ariaLabel?: string;
  id?: string;
  role?: "dialog" | "menu";
  /** Popover placement, defaults to 'bottom'. */
  placement?: Placement;
  /** The axis that runs along the side of the floating element. Represents the distance (gutter or margin) between the floating element and the reference element.
   * https://floating-ui.com/docs/offset#mainaxis
   */
  offsetMainAxis?: number;
  /** The axis that runs along the alignment of the floating element. Represents the skidding between the floating element and the reference element.
   * https://floating-ui.com/docs/offset#crossaxis
   */
  offsetCrossAxis?: number;
  /** Override default styling */
  style?: KivraCSSObject;
  /** Close popper by pressing tab, defaults to true */
  closeOnTab?: boolean;
}

export const Popper = ({
  children,
  anchorRef,
  ariaLabel,
  id,
  dataTestId,
  isOpen,
  onClose,
  placement = "bottom",
  offsetMainAxis = 0,
  offsetCrossAxis = 0,
  style,
  closeOnTab = true,
  role = "dialog",
}: PopperProps): React.JSX.Element | null => {
  const handleClose = (): void => {
    const anchorEl = anchorRef.current;
    onClose();
    anchorEl?.focus();
  };
  const backDropRef = useRef<HTMLDivElement | null>(null);
  const hasTransitionedIn = useMountTransition(isOpen, 300);
  const isDarkMode = useIsDarkMode();

  const {
    x,
    y,
    refs: { setReference, setFloating },
    strategy,
  } = useFloating({
    placement,
    middleware: [
      floatingOffset({
        mainAxis: offsetMainAxis,
        crossAxis: offsetCrossAxis,
      }),
      flip(),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  useDismiss({
    closeOnClick: "inside",
    handler: handleClose,
    enabled: isOpen,
    reference: backDropRef,
    escapeKey: true,
    tabKey: closeOnTab,
  });

  useLayoutEffect(() => {
    if (anchorRef.current) setReference(anchorRef.current);
  }, [anchorRef]);

  return hasTransitionedIn || isOpen
    ? (
      <>
        <PopperBackDrop aria-hidden="true" ref={backDropRef} />
        <StyledPopper
          aria-label={ariaLabel}
          aria-modal={role === "dialog" ? true : undefined}
          coordinates={{ x, y }}
          data-test-id={dataTestId}
          hasTransitionedIn={hasTransitionedIn}
          id={id ?? "popover"}
          isDarkMode={isDarkMode}
          isOpen={isOpen}
          overrideStyle={style}
          ref={setFloating}
          role={role}
          strategy={strategy}
        >
          {children}
        </StyledPopper>
      </>
    )
    : null;
};

const PopperBackDrop = styled.div({
  top: "0px",
  left: "0px",
  right: "0px",
  bottom: "0px",
  zIndex: zIndex.popper - 1,
  position: "fixed",
});

const StyledPopper = styled(StyledPopoverBase)<{
  coordinates: { x: number | null; y: number | null };
  hasTransitionedIn: boolean;
  isDarkMode: boolean;
  isOpen: boolean;
  overrideStyle?: KivraCSSObject;
  strategy: Strategy;
}>(
  ({
    coordinates,
    hasTransitionedIn,
    isDarkMode,
    isOpen,
    overrideStyle,
    strategy,
  }) => ({
    border: isDarkMode ? "solid 1px $border-subtle" : undefined,
    borderRadius: "$radius-medium",
    boxShadow: "$shadow-floating",
    left: coordinates.x ?? "",
    minWidth: "232px",
    width: "max-content",
    opacity: isOpen && hasTransitionedIn ? 1 : 0,
    position: strategy,
    top: coordinates.y ?? "",
    transition: "opacity 75ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    zIndex: zIndex.popper,
    "& > li:first-of-type": {
      borderTopLeftRadius: "$radius-medium",
      borderTopRightRadius: "$radius-medium",
    },
    "& > li:last-of-type": {
      borderBottomLeftRadius: "$radius-medium",
      borderBottomRightRadius: "$radius-medium",
    },
    ...overrideStyle,
  }),
);
